import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { formatDate } from "utils";
import TextFieldComponent from "components/input/TextFieldComponent";
import { Box, Checkbox, FormControlLabel, styled, Typography, useMediaQuery } from "@mui/material";
import { USER_TYPE } from "constants/user-type";
import { IUpsertUserRequest, User } from "../types/user";
import useUserList from "../hooks/useUserList";
import UserFormModal from "../components/UserFormModal";

type TUpsertUser = "create" | "update";

const SearchFieldLabel = styled("div")({
  backgroundColor: "var(--primary-500)",
  color: "var(--black)",
  padding: "10px",
  borderRadius: "4px",
  marginRight: "5px",
  whiteSpace: "nowrap",
  display: "inline-block",
  width: "220px",
  textAlign: "center",
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  border: "1px solid white",
  textAlign: "center",
  fontWeight: "bold",
  fontSize: 16,
  [theme.breakpoints.up("md")]: { fontSize: 18 },
  [theme.breakpoints.up("lg")]: { fontSize: 20 },
}));

const StyledBodyTableCell = styled(TableCell)(({ theme }) => ({
  textAlign: "center",
  fontSize: 16,
  [theme.breakpoints.up("md")]: { fontSize: 18 },
  [theme.breakpoints.up("lg")]: { fontSize: 20 },
}));

const StyledCategoryBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ active }: { active?: boolean }) => ({
  color: "var(--gray-700)",
  fontSize: 23,
  fontWeight: "medium",
  padding: "9px 28px",
  border: "1px solid var(--gray-250)",
  borderRadius: "4px",
  boxShadow: "0px 2px 6px var(--shadow-sm-overlay)",
  cursor: "pointer",
  backgroundColor: active ? "var(--green-400)" : "var(--white)",
}));

/**
 * UserList component displays a list of users with pagination and filtering options.
 * It includes a search form, category filters, and a table to display user details.
 * @example
 * return (
 *   <UserList />
 * )
 * @returns {Element} The rendered component.
 * @remarks
 * - The component uses infinite scrolling to load more users as the user scrolls down.
 * - It includes a modal to display detailed information about a selected user.
 * - The search form allows filtering users by name, email, and type ID.
 * - The component uses Material-UI for styling and layout.
 * @event handleDetailClick - Opens the user detail modal with the selected user's information.
 * @event handleCloseModal - Closes the user detail modal and clears the selected user.
 * @event handleCheckbox - Updates the state of the "Available ID only" checkbox.
 * @event handleScroll - Handles the infinite scrolling to load more users.
 */
export default function UserList() {
  const navigate = useNavigate();
  const {
    users,
    hasMore,
    setTempQuery,
    tempQuery,
    totalItems,
    onNextPage,
    onChangeCategoryType,
    handleSubmitSearch,
    setErr,
    disableSubmit,
    isFetching,
    handleUpdateUser,
    handleCreateUser,
    getSuggestCode,
    suggestCodeList,
    setSuggestCodeList,
  } = useUserList();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isAvailableId, setIsAvailableId] = useState(true);
  const [visibleModal, setVisibleModal] = useState({ create: false, update: false });
  const matchesPC = useMediaQuery("(min-width:1024px)");
  const categories = [
    { id: "C0001", label: "本部" },
    { id: "C0006", label: "企業" },
    { id: "C0002", label: "店舗" },
    { id: "C0003", label: "得意先" },
    { id: "C0004", label: "仕入先" },
    { id: "C0005", label: "倉庫" },
  ];
  const fontSize = { fontSize: { xs: 18, lg: 23 } };

  const handleDetailClick = (user: User) => {
    setSelectedUser(user);
    setVisibleModal((prev) => ({ ...prev, update: true }));
  };

  const handleCloseUpsertModal = (type: TUpsertUser) => {
    setVisibleModal((prev) => ({ ...prev, [type]: false }));
    if (type === "create") setSuggestCodeList([]);
    setSelectedUser(null);
  };

  const handleUpsertUser = async (type: TUpsertUser, data: IUpsertUserRequest) => {
    if (type === "create") await handleCreateUser(data);
    else {
      const updateData: IUpsertUserRequest = {
        ...data,
        id: selectedUser?.id,
        delete_flag: selectedUser?.delete_flag ? 1 : 0,
      };
      await handleUpdateUser(updateData);
    }
    handleCloseUpsertModal(type);
  };

  const submitSearch = async () => {
    if (disableSubmit) return;
    await handleSubmitSearch();
  };

  const handleChangeCategory = (id: string) => {
    setIsAvailableId(false);
    onChangeCategoryType(id);
  };

  const handleCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsAvailableId(event.target.checked);
    setTempQuery({ ...tempQuery, status_flag: event.target.checked ? 1 : 0 });
  };

  useEffect(() => {
    const handleScroll = () => {
      if (
        Math.round(window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 &&
        hasMore === 1 &&
        !isFetching
      ) {
        onNextPage();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, isFetching, onNextPage]);

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 2.5, lg: 5 }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate(-1)} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box sx={{ p: { xs: 2, md: 2.5, lg: 5 } }}>
        <Box
          sx={{
            backgroundColor: "var(--white)",
            display: "flex",
            flexWrap: "wrap",
            gap: { xs: 3, md: 4, lg: 10 },
            pb: 5,
          }}
        >
          <Box
            sx={{
              textAlign: "right",
              display: "inline-block",
            }}
          >
            <Typography
              variant={matchesPC ? "h4" : "h5"}
              margin={0}
              textAlign="right"
              fontSize={{ xs: 24, md: 28, lg: 23 }}
              color="var(--gray-700)"
            >
              マスタ管理
            </Typography>
            <Typography
              variant={matchesPC ? "h5" : "h6"}
              margin={0}
              textAlign="right"
              fontSize={{ xs: 20, md: 28 }}
              color="var(--gray-700)"
            >
              利用者一覧
            </Typography>
            <Button
              variant="outlined"
              startIcon={<img src="/img/user/file.svg" width="23px" height="23px" alt="閉じる" />}
              sx={{
                border: "1px solid var(--gray-250)",
                height: "50px",
                width: "180px",
                fontSize: { xs: 20, md: 23 },
                color: "var(--gray-700)",
                fontWeight: "medium",
                marginTop: "25px",
                marginLeft: "30px",
              }}
              onClick={() => setVisibleModal((prev) => ({ ...prev, create: true }))}
            >
              新規登録
            </Button>
          </Box>
          <Box width={{ xs: "100%", md: "75%" }}>
            <Box
              display="flex"
              columnGap="10px"
              rowGap="10px"
              margin="0 auto"
              marginBottom="20px"
              flexWrap="wrap"
              alignItems="center"
            >
              <SearchFieldLabel
                sx={{
                  fontSize: { xs: 16, md: 18, lg: 20 },
                  width: { xs: 222, lg: 213, xl: 193 },
                }}
              >
                種類
              </SearchFieldLabel>
              <Box
                display="flex"
                columnGap="10px"
                rowGap="10px"
                sx={{ flexWrap: { xs: "wrap", md: "wrap", lg: "nowrap" } }}
              >
                {categories
                  .reduce(
                    (rows, category, index) => {
                      if (index % 2 === 0) rows.push([] as { id: string; label: string }[]);
                      rows[rows.length - 1].push(category);
                      return rows;
                    },
                    [] as { id: string; label: string }[][],
                  )
                  .map((row) => (
                    <Box
                      key={row.map((category) => category.id).join("-")}
                      display="flex"
                      columnGap="10px"
                    >
                      {row.map(({ id, label }) => (
                        <StyledCategoryBox
                          key={id}
                          active={tempQuery.user_type === id}
                          onClick={() => handleChangeCategory(id)}
                          sx={fontSize}
                        >
                          {label}
                        </StyledCategoryBox>
                      ))}
                    </Box>
                  ))}
              </Box>
            </Box>
            <Box
              display="flex"
              gap="10px"
              marginBottom="20px"
              margin="0 auto"
              sx={{
                flexWrap: { xs: "wrap", md: "wrap", lg: "wrap", xl: "nowrap" },
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  width: { xs: "100%", lg: "440px" },
                }}
              >
                <SearchFieldLabel sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }}>
                  所属
                </SearchFieldLabel>
                <TextFieldComponent
                  value={tempQuery.affiliation}
                  onChangeText={(value) => setTempQuery({ ...tempQuery, affiliation: value })}
                  placeholder="所属"
                  sxInput={{ height: "44px" }}
                  sxRoot={{ borderRadius: "4px" }}
                />
              </Box>
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  width: { xs: "100%", lg: "440px" },
                }}
              >
                <SearchFieldLabel
                  sx={{
                    fontSize: {
                      xs: 16,
                      md: 18,
                      lg: 20,
                    },
                  }}
                >
                  利用者名
                </SearchFieldLabel>
                <TextFieldComponent
                  value={tempQuery.user_name}
                  onChangeText={(value) => setTempQuery({ ...tempQuery, user_name: value })}
                  placeholder="利用者名"
                  sxInput={{ height: "44px" }}
                  sxRoot={{ borderRadius: "4px" }}
                />
              </Box>
              <Box display="flex" alignItems="center" sx={{ width: { xs: "100%", lg: "440px" } }}>
                <SearchFieldLabel sx={{ fontSize: { xs: 16, md: 18, lg: 20 } }}>
                  メールアドレス
                </SearchFieldLabel>
                <TextFieldComponent
                  value={tempQuery.email_address}
                  onChangeText={(value) => setTempQuery({ ...tempQuery, email_address: value })}
                  placeholder="メールアドレス"
                  sxInput={{ height: "44px" }}
                  sxRoot={{ borderRadius: "4px" }}
                  onError={(error) => setErr(error)}
                  clearError={tempQuery.email_address.trim() === ""}
                  validateOption={{
                    email: {
                      message: "",
                    },
                  }}
                />
              </Box>

              <FormControlLabel
                control={
                  <Checkbox checked={isAvailableId} onChange={handleCheckbox} color="secondary" />
                }
                label={
                    <Typography sx={{ fontSize: { xs: 16, md: 18, lg: 20 }, color: "var(--black)" }}>
                    有効のみ
                  </Typography>
                }
                sx={{ whiteSpace: "pre" }}
              />
            </Box>
          </Box>
        </Box>
        <Box textAlign="center" margin="0 auto" sx={{ backgroundColor: "var(--white)" }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "var(--gray-500)",
              color: "var(--white)",
              width: "190px",
              height: "50px",
              fontSize: { xs: 18, md: 20, lg: 25 },
              marginBottom: "20px",
              borderRadius: "9px",
              ":hover": {
                backgroundColor: "var(--gray-250)",
                boxShadow: "var(--shadow)",
              },
            }}
            onClick={submitSearch}
          >
            検索
          </Button>
        </Box>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{ p: { xs: 2, md: 2.5, lg: 5 }, backgroundColor: "var(--offwhite-100)" }}
      >
        <Box
          display="flex"
          justifyContent="end"
          fontSize={{ xs: 16, md: 18, lg: 20 }}
          sx={{ width: "100%", pr: { xs: 0, lg: "50px" }, mb: "15px" }}
        >
          件数【{totalItems}件】
        </Box>
        <Box sx={{ overflowX: "auto", width: "100%" }}>
          <Table sx={{ minWidth: 1460, width: "100%" }}>
            <TableHead sx={{ backgroundColor: "var(--primary-500)" }}>
              <TableRow>
                <StyledTableCell sx={{ width: "115px" }}>種別</StyledTableCell>
                <StyledTableCell sx={{ width: "265px" }}>
                  所属 <br /> (店舗/企業/得意先名)
                </StyledTableCell>
                <StyledTableCell sx={{ width: "192px" }}>利用者名</StyledTableCell>
                <StyledTableCell sx={{ width: "209px" }}>
                  メールアドレス <br /> (ログイン用メールアドレス)
                </StyledTableCell>
                <StyledTableCell sx={{ width: "130px" }}>有効期限</StyledTableCell>
                <StyledTableCell sx={{ width: "180px" }}>最終ログイン日</StyledTableCell>
                <StyledTableCell sx={{ width: "209px" }}>詳細</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <TableRow key={`${user.id}_${index}`} className={index % 2 === 0 ? "even" : "odd"}>
                  <StyledBodyTableCell>
                    {USER_TYPE[user.user_type as keyof typeof USER_TYPE]}
                  </StyledBodyTableCell>
                  <StyledBodyTableCell sx={{ textAlign: "left" }}>
                    {user.affiliation}
                  </StyledBodyTableCell>
                  <StyledBodyTableCell sx={{ textAlign: "left" }}>
                    {user.user_name}
                  </StyledBodyTableCell>
                  <StyledBodyTableCell sx={{ textAlign: "left" }}>
                    {user.email_address}
                  </StyledBodyTableCell>
                  <StyledBodyTableCell>
                    {user.exp_date ? formatDate(user.exp_date) : ""}
                  </StyledBodyTableCell>
                  <StyledBodyTableCell>
                    {user.last_login ? formatDate(user.last_login) : ""}
                  </StyledBodyTableCell>
                  <StyledBodyTableCell>
                    <Button
                      variant="outlined"
                      onClick={() => handleDetailClick(user)}
                      sx={{
                        border: "1px solid var(--gray-250)",
                        backgroundColor: "var(--white)",
                        color: "var(--black)",
                        fontSize: { xs: 16, md: 18, lg: 20 },
                        borderRadius: "4px",
                        width: { xs: "100%", xl: "83%" },
                        height: "40px",
                        whiteSpace: "pre",
                      }}
                    >
                      詳細
                    </Button>
                  </StyledBodyTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {visibleModal.create && (
        <UserFormModal
          type="create"
          isLoading={isFetching}
          onHandleSubmit={(data: IUpsertUserRequest) => handleUpsertUser("create", data)}
          getSuggestCode={getSuggestCode}
          suggestCodeList={suggestCodeList}
          visible={visibleModal.create}
          onClose={() => handleCloseUpsertModal("create")}
        />
      )}

      {visibleModal.update && (
        <UserFormModal
          type="update"
          isLoading={isFetching}
          onHandleSubmit={(data: IUpsertUserRequest) => handleUpsertUser("update", data)}
          getSuggestCode={getSuggestCode}
          suggestCodeList={suggestCodeList}
          visible={visibleModal.update}
          onClose={() => handleCloseUpsertModal("update")}
          userDetail={selectedUser!}
        />
      )}
    </>
  );
}
